import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useContext, useState } from "react"
import api from "~/api2"
import { AdminContext } from "~/contexts/AdminContext"
import { queryKeys } from "~/constants/queryKeys"

interface RevertPaymentParams {
  paymentId: string
  reservationId: string
}

const useRevertPayment = ({
  reservationId,
  orderId,
  refetchPreview,
}: {
  reservationId?: string
  orderId?: string
  refetchPreview: () => void
}) => {
  const { showNotification } = useContext(AdminContext)
  const [showSuccess, setShowSuccess] = useState(false)
  const queryClient = useQueryClient()

  const revertPaymentMutation = useMutation({
    mutationFn: ({ paymentId, reservationId }: RevertPaymentParams) => {
      return api.Payments.revert(paymentId, reservationId)
    },
    onSuccess: () => {
      setShowSuccess(true)

      setTimeout(() => {
        setShowSuccess(false)
      }, 2000)

      queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER, reservationId] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER, orderId] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.HISTORY, reservationId] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.RESERVATION_PREVIEWS, orderId] })
      refetchPreview()
    },
    onError: (error: any) => {
      showNotification({
        type: "error",
        title: "Failed to revert payment",
        description: error.message,
        dismissAfter: 5000,
      })
      console.error(error)
    },
  })

  const revertPayment = (paymentId: string) => {
    if (!reservationId) return
    revertPaymentMutation.mutate({ paymentId, reservationId })
  }

  return {
    revertPayment,
    isLoading: revertPaymentMutation.isPending,
    showSuccess,
    isError: revertPaymentMutation.isError,
    error: revertPaymentMutation.error,
  }
}

export default useRevertPayment
